import { AddDataSourceModal, Button } from "components";
import EditDataSourcesModal from "components/Datasources/modals/EditDataSourcesModal";
import { Modal } from "components/shared";
import Loading from "components/shared/Loading";
import Typography from "components/shared/Typography";
import { Formik } from "formik";
import { ISelectedDataSource } from "pages/block/edit-block";
import { useEffect, useMemo, useState } from "react";
import { TfiDownload } from "react-icons/tfi";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router";
import { useFetchDataSources, useGetReportDataSources, useGetReportDetails, useUpdateReport } from "services";
import { IDataSource } from "types";
import { convertArrayToObjectOfObjects, convertStringArrayToObject, customId, formikHelper, getIcon } from "utils";
import { offset, useClick, useFloating, useInteractions } from "@floating-ui/react";
import { FaAngleDown, FaAngleUp, FaArrowLeftLong } from "react-icons/fa6";
import { useResizeDetector } from "react-resize-detector";
import classNames from "classnames";
import { IoMdClose } from "react-icons/io";
import { GoPlus } from "react-icons/go";
import { toast } from "react-toastify";
import { queryClient } from "index";

export default function EditReport() {
  const param = useParams();
  const navigate = useNavigate();
  const { id } = param;
  const { data: reportData, isFetching: isReportDetailsFetching, error: isReportDetailsError } = useGetReportDetails(id as string);

  const {
    data: reportDatasources,
    error: isErrorReportDatasourceError,
    isFetching: isFetchingReportDataSources,
  } = useGetReportDataSources(id as string);

  const [currentPage, setCurrentPage] = useState<number>(1);

  const [dataTotal, setDataTotal] = useState(0);

  const trigger = dataTotal > 0;

  const query = {
    page: String(currentPage),
    triggerFetch: trigger || false,
  };

  const { data, isFetching, error } = useFetchDataSources(query);

  const [radioValue, setRadioValue] = useState<any>(undefined);
  const dataFetchingError = isReportDetailsError || error;
  const dataFetchingLoading = isReportDetailsFetching || isFetching;

  const displayNoDataSourcesAvailable = data && data.data.data_sources.length === 0;

  const sources = data && data?.data?.data_sources;

  const reportSources = reportDatasources && reportDatasources?.data?.data_sources;

  const displayDataSources = sources && sources.length > 0 && !isFetching;

  const [selectedDataSource, setSelectedDataSource] = useState<ISelectedDataSource | null>(null);

  const [selectedDataSources, setSelectedDataSources] = useState<Partial<IDataSource>[]>([]);

  const [dataSourcesmodalOpen, setDataSourcesModalOpen] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const reportDataSources = useMemo(() => reportSources || [], [reportDatasources]);

  useEffect(() => {
    if (reportData) {
      setSelectedDataSources(reportDataSources);
    }
  }, [reportData, reportDataSources]);

  const updateDataSource = (data_source: Partial<ISelectedDataSource>) => {
    setRadioValue(data_source);
    setSelectedDataSource(data_source as ISelectedDataSource);
  };

  const handleDataSelection = async (item: Partial<IDataSource>) => {
    try {
      let checkedDataSourceState = [...selectedDataSources];
      const dataCheck = checkedDataSourceState.find((data) => data.id === item.id);

      if (dataCheck) {
        return;
      } else {
        checkedDataSourceState = checkedDataSourceState.concat(item);
      }

      setSelectedDataSources(checkedDataSourceState);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteingSelection = async (item: Partial<IDataSource>) => {
    try {
      const checkedDataSourceState = selectedDataSources.filter((data) => data.id !== item.id);
      setSelectedDataSources(checkedDataSourceState);
    } catch (e) {
      console.error(e);
    }
  };

  const { updateReport } = useUpdateReport();

  const updatedSources = [...selectedDataSources.map((source) => source.id)] as string[];

  const data_sources = convertStringArrayToObject(updatedSources);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    const data = {
      report: {
        ...values,
        ...data_sources,
      },
      report_id: id as string,
    };

    updateReport(data, {
      onError: (error, variables, context) => {
        setIsSubmitting(false);
        toast.error("Error updating Report", {
          position: "top-center",
          toastId: customId,
          autoClose: 2000,
          theme: "colored",
        });
      },
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({ queryKey: ["report"] });
        toast.success(`Report updated Successfully`, {
          position: "top-center",
          toastId: customId,
          autoClose: 1000,
          theme: "colored",
        });
        navigate(`/reports/${id}`);
      },
    });
  };
  return (
    <div className="pb-[69px] max-w-[1400px]">
      <div className="flex justify-start">
        <div className="flex gap-3 items-center hover:cursor-pointer mb-8 " onClick={() => window.history.back()}>
          <FaArrowLeftLong />{" "}
          <Typography variant="body2" className="text-grey-900">
            Go Back
          </Typography>
        </div>
      </div>
      <div className="mb-6">
        <Typography variant="displayxs" className="text-shade-black text-left !font-bold">
          Edit Report
        </Typography>
      </div>

      <div className="flex gap-6 w-full">
        <div className="w-[450px]">
          {dataFetchingError && (
            <div className="flex justify-center items-center w-full h-full">
              {" "}
              <Typography variant="displaylg" className="text-grey-600 text-center">
                {" "}
                There was an unexpected error while retrieving block
              </Typography>
            </div>
          )}
          {isReportDetailsFetching && (
            <div className="">
              <Skeleton count={6} height={50} className="mb-4" />
            </div>
          )}
          {reportData && data && !dataFetchingLoading && (
            <Formik
              initialValues={{
                title: reportData?.data?.report?.title || "",
              }}
              onSubmit={handleSubmit}
            >
              {(formik) => (
                <>
                  <form className="" onSubmit={formik.handleSubmit} noValidate>
                    {dataSourcesmodalOpen && (
                      <Modal
                        onClose={() => setDataSourcesModalOpen(false)}
                        open={dataSourcesmodalOpen}
                        variant="full"
                        children={
                          <EditDataSourcesModal
                            value={radioValue}
                            data={sources as Partial<IDataSource>[]}
                            setRadioValue={updateDataSource}
                            onClose={setDataSourcesModalOpen}
                            handleDataSelection={handleDataSelection}
                            selectedDataSources={selectedDataSources}
                          />
                        }
                      />
                    )}
                    <div className="flex flex-col w-full gap-8 border-[1.2px] py-10 px-5 border-grey-200 rounded-md">
                      <div className="flex flex-col gap-2">
                        <Typography variant="body2" className="text-shade-black text-left !font-semibold">
                          Report Title
                        </Typography>
                        <input
                          className="py-4 mt-[2px] px-4 bg-white border shadow-sm border-grey-300 placeholder-grey-400 focus:outline-none focus:border-primary-200 focus:ring-primary-200 block w-full rounded-md sm:text-sm focus:ring-1"
                          placeholder="Report Title"
                          {...formik.getFieldProps("title")}
                          {...formikHelper(formik, "title")}
                          onChange={(event) => {
                            formik.setFieldValue("title", event.target.value);
                          }}
                        />
                        {formik.errors.title && formik.touched.title && (
                          <Typography variant="textxs" className="text-error-500 text-left !font-semibold">
                            Report title is required
                          </Typography>
                        )}
                      </div>

                      <div className="flex justify-start flex-col gap-4">
                        <div className="flex items-center gap-2">
                          <div
                            className={classNames("text-primary-700 flex gap-1 items-center hover:cursor-pointer")}
                            onClick={() => setDataSourcesModalOpen(true)}
                          >
                            <div className="flex items-center gap-2">
                              <Typography variant="body2" className="!font-semibold text-shade-black">
                                Data Sources
                              </Typography>
                            </div>
                          </div>
                        </div>
                        <div className="border-t-[1px] border-grey-200" />
                        {selectedDataSources.map((dataSource, index) => (
                          <div key={index} className="w-full flex items-center justify-between gap-2">
                            <div className="truncate">{dataSource.name}</div>
                            <div
                              className="h-8 !w-8 rounded-full hover:bg-grey-100 hover:cursor-pointer flex items-center justify-center"
                              onClick={() => handleDeleteingSelection(dataSource as Partial<IDataSource>)}
                            >
                              <IoMdClose className="text-error-500" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between mt-10">
                      <Button
                        onClick={async () => {
                          const validation = await formik.validateForm();
                          if (Object.keys(validation).length) return;
                        }}
                        isSubmitting={formik.isSubmitting || isSubmitting}
                        type="submit"
                        title="Update"
                        disabled={formik.isSubmitting || !formik.isValid || selectedDataSources.length === 0 || isSubmitting}
                        fullwidth
                      />
                    </div>
                  </form>
                </>
              )}
            </Formik>
          )}
        </div>
        <div className="w-[500px] pt-10 pb-4  border-[1.2px] border-grey-200 rounded-md max-h-[600px] overflow-y-auto">
          <div className="flex justify-between items-center px-5">
            <Typography variant="textxl" className="!font-semibold text-shade-black text-left">
              Available Data Sources
            </Typography>

            <div className="" onClick={() => navigate(`/data-sources/upload?report_id=${id}`)}>
              <Button icon={<TfiDownload />} title="Upload Another file" type="submit" />
            </div>
          </div>
          <div className="px-5">
            {isFetching && (
              <div className="mt-4">
                <div className="flex justify-between items-center  mb-8">
                  <div className="w-40">
                    <Loading numberOfLoaders={1} className="bg-grey-200 !h-12" />
                  </div>
                  <div className="w-48">
                    <Loading numberOfLoaders={1} className="bg-grey-200 !h-12" />
                  </div>
                </div>{" "}
                <div className="">
                  <Skeleton count={4} height={50} className="mb-4" />
                </div>
              </div>
            )}
            {displayNoDataSourcesAvailable && !isFetching && (
              <div className="flex justify-center items-center py-[150px]">
                <Typography variant="displayxs" weight="semibold">
                  No data source available
                </Typography>{" "}
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-4 flex-col px-5">
            {displayDataSources &&
              sources?.map((dataSource: Partial<IDataSource>) => (
                <div key={dataSource.id} className="">
                  <div
                    className="flex gap-4 items-center  hover:cursor-pointer hover:bg-grey-100 px-[2px] py-2"
                    onClick={() =>
                      handleDataSelection({ id: dataSource?.id as string, name: dataSource?.name as string, type: dataSource?.type as string })
                    }
                  >
                    <img src={getIcon(dataSource.type as string)} alt="pdf" className="h-10 w-10" />
                    <Typography variant="body2" className="!font-semibold">
                      {dataSource.name}
                    </Typography>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
